import { FC, useContext, useRef, useState, useCallback, useMemo } from "react";
import ColorPicker from "../../Shared/ColorPicker";
import ColumnWeightInput from "../ColumnWeightInput/ColumnWeightInput";
import ColumnStatusChange from "../ColumnStatusChange/ColumnStatusChange";
import EditPen from "../../../assets/edit.svg";
import { IFunctionPermissions } from "../../../common/api_interfaces/role/IFunctionPermissions";
import { AppContext } from "../../../App";
import { hasPermission } from "../../../helpers/hasPermisson";
import { BoardContext } from "../../../contexts/BoardContext";
import { IColumnDisplay } from "../../../common/api_interfaces/column/IColumnDisplay";

interface IBoardColumnOptionsProps {
    column: IColumnDisplay;
    userAccess: IFunctionPermissions[];
}

const BoardColumnOptions: FC<IBoardColumnOptionsProps> = ({ column, userAccess }) => {
    const [editToProxy, setEditToProxy] = useState<boolean>(false);
    const [existingBoard, setExistingBoard] = useState<boolean>(false);
    const [proxyBoardName, setProxyBoardName] = useState<string>(column.name);
    const [editColumnName, setEditColumnName] = useState(false);
    
    const inputRef = useRef<HTMLInputElement | null>(null);
    const { showToast } = useContext(AppContext);    
    const boardContext = useContext(BoardContext);

    const [selectedBoardId, setSelectedBoardId] = useState<number | undefined>(
        boardContext.boardsForProxy[0]?.id
    );

    const { addProxyColumn, deleteProxyColumn } = useMemo(() => ({
        addProxyColumn: hasPermission(userAccess, "ColumnProxyAction", "create"),
        deleteProxyColumn: hasPermission(userAccess, "ColumnProxyAction", "delete")
    }), [userAccess]);

    const handleChangeName = useCallback((e: React.KeyboardEvent, columnId: number) => {
        if (e.key === "Enter") {
            setEditColumnName(false);
            boardContext.handleEditColumn(columnId, (e.target as HTMLInputElement).value);
        }
    }, [boardContext.handleEditColumn]);

    const handleChangeColor = useCallback((color: string, columnId: number) => {
        boardContext.handleEditColumn(columnId, undefined, color);
    }, [boardContext.handleEditColumn]);

    const handleChangeProxyBoardName = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setProxyBoardName(e.target.value);
    }, []);

    const onMakeProxy = useCallback(() => {
        if (existingBoard) {
            if (!selectedBoardId) {
                alert("Нет id доски");
                return;
            }
            boardContext.handleMakeColumnProxy(column.id, undefined, selectedBoardId);
        } else {
            if (!proxyBoardName || proxyBoardName.length <= 0) {
                alert("Нет названия доски");
                return;
            }
            boardContext.handleMakeColumnProxy(column.id, proxyBoardName, undefined);
        }
        setEditToProxy(false);
        setExistingBoard(false);
    }, [existingBoard, selectedBoardId, proxyBoardName, column.id, boardContext.handleMakeColumnProxy]);

    const onMakeCommon = useCallback(() => {
        boardContext.handleMakeColumnCommon(column.id);
    }, [column.id, boardContext.handleMakeColumnCommon]);

    const handleSaveWeight = useCallback((weight: number) => {
        boardContext.handleEditColumn(column.id, undefined, undefined, weight);
    }, [column.id, boardContext.handleEditColumn]);

    const handleEditStatus = useCallback((status: number) => {
        boardContext.handleEditColumn(column.id, undefined, undefined, undefined, status);
    }, [column.id, boardContext.handleEditColumn]);

    const focusInput = useCallback(() => {
        setEditColumnName(prev => !prev);
        setTimeout(() => {
            inputRef.current?.focus();
        }, 0);
    }, []);

    const handleBoardSelectChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedBoardId(Number(e.target.value));
    }, []);

    const handleToggleProxyEdit = useCallback(() => {
        if (addProxyColumn) {
            setEditToProxy(prev => !prev);
        } else {
            showToast("У вас нет доступа на создание прокси колонки!");
        }
    }, [addProxyColumn, showToast]);

    const handleMakeCommonWithCheck = useCallback(() => {
        if (deleteProxyColumn) {
            onMakeCommon();
        } else {
            showToast("У вас нет доступа на удаление прокси колонки!");
        }
    }, [deleteProxyColumn, onMakeCommon, showToast]);

    const proxyButtonStyle = useMemo(() => ({
        backgroundColor: addProxyColumn ? undefined : "rgb(175, 175, 175)"
    }), [addProxyColumn]);

    const deleteProxyButtonStyle = useMemo(() => ({
        backgroundColor: deleteProxyColumn ? undefined : "rgb(175, 175, 175)"
    }), [deleteProxyColumn]);

    const renderProxySection = useMemo(() => {
        if (boardContext.isProxyBoard) return null;
        
        if (column.state !== 0) {
            return (
                <div className="column__popup--widget__settings--proxy">
                    <div className="column__popup--widget__settings--proxy__add">
                        {editToProxy && (
                            <div>
                                {existingBoard ? (
                                    <select onChange={handleBoardSelectChange}>
                                        {boardContext.boardsForProxy.map((b) => (
                                            <option key={b.id} value={b.id}>
                                                {b.name}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    <div className="column__popup--widget__settings--proxy__name__board">
                                        <input
                                            value={proxyBoardName}
                                            onChange={handleChangeProxyBoardName}
                                            placeholder="Название доски"
                                        />
                                    </div>
                                )}
                                <button onClick={onMakeProxy}>Сохранить</button>
                            </div>
                        )}
                    </div>
                    {column.proxyBoardId ? (
                        <button
                            style={deleteProxyButtonStyle}
                            onClick={handleMakeCommonWithCheck}
                        >
                            Сделать обычной
                        </button>
                    ) : (
                        <button
                            style={proxyButtonStyle}
                            onClick={handleToggleProxyEdit}
                        >
                            {editToProxy ? "Закрыть" : "Сделать прокси"}
                        </button>
                    )}
                </div>
            );
        }
        return null;
    }, [
        boardContext.isProxyBoard, 
        column.state, 
        column.proxyBoardId, 
        editToProxy, 
        existingBoard,
        boardContext.boardsForProxy, 
        proxyBoardName, 
        deleteProxyButtonStyle, 
        proxyButtonStyle,
        handleBoardSelectChange, 
        handleChangeProxyBoardName, 
        onMakeProxy,
        handleMakeCommonWithCheck, 
        handleToggleProxyEdit
    ]);

    return (
        <div className="column-settings-popup__position">
            <div className="column-settings-popup">
                <div className="column-settings-popup__name">Имя</div>
                <div className="column-settings-popup__input--edit">
                    <input
                        disabled={!editColumnName}
                        ref={inputRef}
                        defaultValue={column.name}
                        onKeyDown={(e) => handleChangeName(e, column.id)}
                        type="text"
                        name="column-options-name-field"
                    />
                    <img src={EditPen} onClick={focusInput} alt="Edit" />
                </div>
                
                <div className="column-settings-popup__block--color">
                    <div className="column-settings-popup__block--color--name">Цвет колонки</div>
                    <div className="column-settings-popup__block--color--block">
                        <ColorPicker
                            onColorSelected={(color) => handleChangeColor(color, column.id)}
                            options={boardContext.colorOptions}
                            defaultColor={column.color}
                        />
                    </div>
                </div>
                
                {!boardContext.editStatusAcess && (
                    <ColumnWeightInput 
                        column={column} 
                        onEditWeight={handleSaveWeight} 
                        edit={true} 
                    />
                )}
                
                {boardContext.editStatusAcess && (
                    <ColumnStatusChange 
                        column={column} 
                        onEditStatus={handleEditStatus} 
                        edit={boardContext.editStatusAcess} 
                    />
                )}
                
                {renderProxySection}
            </div>
        </div>
    );
};

export default BoardColumnOptions;